import { ref, computed } from 'vue'
import { jwtDecode } from 'jwt-decode'
import type { JWTPayload } from 'jose'
import { useRuntimeConfig } from '#app'
import type { Auth, TokenResponse, AuthConfig } from '../types'
import { isPlatform } from '@ionic/vue';
import AppAuth from '../impl/AppAuth';
import KeycloakJs from '../impl/KeycloakJs';

let auth: Auth | undefined
const tokenResponse = ref<TokenResponse>()

let authenticatedCallback: ((response: TokenResponse) => Promise<void>) | undefined
let logoutCallback: (() => Promise<void>) | undefined

export function setAuthenticatedCallback(cb: (response: TokenResponse) => Promise<void>) {
	authenticatedCallback = cb
}

export function setLogoutCallback(cb: () => Promise<void>) {
	logoutCallback = cb
}

async function init(config: { url: string, realm: string, clientId: string, redirectUri: string, appRedirectUri: string, environment: string }) {
	if (isPlatform('capacitor')) {
		auth = new AppAuth()
	} else {
		auth = new KeycloakJs()
	}

	try {
		const authConfig: AuthConfig = {
			url: config.url,
			realm: config.realm,
			clientId: config.clientId,
			redirectUri: isPlatform('capacitor') ? config.appRedirectUri : config.redirectUri,
			allowHttp: config.environment === 'local', // Allow HTTP only in local environment
		}

		const response = await auth.init(authConfig)
		if (response?.idToken) {
			tokenResponse.value = response
			await authenticatedCallback?.(response)
		}
	} catch (error) {
		console.error('Auth plugin initialization failed:', error)
		throw createError({
			statusCode: 503,
			statusMessage: 'Auth plugin initialization failed',
			cause: error,
			fatal: true,
		})
	}
}

export default function useAuth() {
	const jwt = computed(() => tokenResponse.value?.idToken)
	const tokenParsed = computed<(JWTPayload & Record<string, unknown>) | undefined>(() => jwt.value ? jwtDecode(jwt.value) : undefined)
	const isAuthenticated = computed(() => !!jwt.value)

	const isExpired = computed(() => {
		if (!tokenParsed.value?.exp) return true
		const now = Math.floor(Date.now() / 1000)
		return tokenParsed.value.exp < now
	})

	const signIn = async () => {
		const response = await auth?.signIn()
		if (response) {
			tokenResponse.value = response
			await authenticatedCallback?.(response)
			console.debug('User signed in', tokenResponse.value)
		}
	}

	const signOut = async () => {
		const idToken = tokenResponse.value?.idToken
		if (idToken) {
			tokenResponse.value = undefined
			await logoutCallback?.()
			await auth?.signOut(idToken)
		} else {
			console.warn('signOut() has been called without an idToken')
		}
	}

	const signUp = async () => {
		return auth?.signUp()
	}

	/**
	 * NUR FUER LOKALE ENTWICKLUNG: Setzt ein vorab beschafftes Keycloak-Token-Set direkt und
	 * triggert den authenticated-Hook (account-sync, refresh) ohne Keycloak-Redirect-Flow.
	 *
	 * Wird verwendet, um den Testnutzer per Dev-Endpoint einzuloggen.
	 *
	 * Safety-Guard: In Nicht-local-Umgebungen (dev/stage/prod) sofortiger No-Op,
	 * damit Dev-Login-Pfade nie versehentlich produktiv aktiv werden.
	 * preview:android/preview:ios laden `.env` mit ENVIRONMENT=local und funktionieren weiterhin.
	 */
	const applyExternalTokens = async (response: TokenResponse) => {
		const environment = useRuntimeConfig().public.environment
		if (environment !== 'local') return
		tokenResponse.value = response
		await authenticatedCallback?.(response)
	}

	const refreshToken = async () => {
		console.debug('Refreshing token...')
		if (tokenResponse.value?.refreshToken) {
			tokenResponse.value = await auth?.refreshToken(tokenResponse.value?.refreshToken)
			console.debug('Token refreshed', tokenResponse.value)
		} else {
			console.warn('refreshToken() has been called without a refreshToken')
		}
	}

	return {
		tokenResponse,
		jwt,
		tokenParsed,
		isAuthenticated,
		isExpired,
		init,
		signIn,
		signOut,
		signUp,
		refreshToken,
		applyExternalTokens,
	}
}
